import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import PageStructure from 'flarum/forum/components/PageStructure';
import IndexSidebar from 'flarum/forum/components/IndexSidebar';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Link from 'flarum/common/components/Link';
const WAITING = ['Searching 20 years of GuitarTalk threads…', 'Reading what members said…', 'Checking what Alan had to say…', 'Writing it up…'];
export default class AskPage extends Page {
  question = '';
  loading = false;
  waitingIndex = 0;
  timer = null;
  error = null;
  answer = null;
  suggestions = [];
  streaming = false;
  streamTimer = null;
  oninit(vnode) {
    super.oninit(vnode);
    app.setTitle('Ask GuitarTalk');
    this.loadSuggestions();
    const slug = m.route.param('slug');
    if (slug) this.open(slug);

    // Arrived from the home page panel with a question: ask it straight away.
    const q = m.route.param('q');
    if (!slug && q) {
      this.question = q;
      this.ask();
    }
    // From a thread's "Ask a follow-up": fill the box in and let them add to it.
    if (!slug && !q && m.route.param('draft')) this.question = m.route.param('draft') + ' ';
  }
  onbeforeupdate(vnode) {
    super.onbeforeupdate?.(vnode);
    const slug = m.route.param('slug');
    if (slug && slug !== this.answer?.slug && !this.loading) this.open(slug);
    if (!slug && this.answer && !this.loading) this.answer = null;
  }
  onremove(vnode) {
    super.onremove(vnode);
    clearInterval(this.timer);
    clearTimeout(this.streamTimer);
  }

  /**
   * Types the (already finished) answer onto the screen word by word, as if the AI were writing it now.
   * Empties every text node, then refills them in order. Skipped for people who prefer reduced motion.
   */
  stream(body) {
    clearTimeout(this.streamTimer);
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return this.finishStream();
    const walker = document.createTreeWalker(body, NodeFilter.SHOW_TEXT);
    const nodes = [];
    for (let n = walker.nextNode(); n; n = walker.nextNode()) {
      nodes.push({
        node: n,
        words: (n.textContent || '').split(/(\s+)/)
      });
      n.textContent = '';
    }
    // Blocks (paragraphs, bullets, lists) stay hidden until their first word arrives.
    body.querySelectorAll('p, li, ul, ol, h1, h2, h3, h4, blockquote, table, .AskMember').forEach(el => el.classList.add('AskPending'));
    const reveal = n => {
      for (let el = n.parentElement; el && el !== body; el = el.parentElement) el.classList.remove('AskPending');
    };
    const caret = document.createElement('span');
    caret.className = 'AskCaret';
    let i = 0;
    let w = 0;
    const tick = () => {
      if (!body.isConnected) return;
      for (let k = 0; k < 3 && i < nodes.length; k++) {
        const cur = nodes[i];
        cur.node.textContent += cur.words[w++] ?? '';
        if (cur.node.textContent.trim()) reveal(cur.node);
        if (w >= cur.words.length) {
          i++;
          w = 0;
        }
      }
      const at = nodes[Math.min(i, nodes.length - 1)]?.node;
      at?.parentNode?.insertBefore(caret, at.nextSibling);
      if (i < nodes.length) this.streamTimer = setTimeout(tick, 28);else {
        caret.remove();
        body.querySelectorAll('.AskPending').forEach(el => el.classList.remove('AskPending'));
        this.finishStream();
      }
    };
    // A short "thinking" beat before the words start.
    caret.style.marginLeft = '0';
    body.appendChild(caret);
    this.streamTimer = setTimeout(tick, 700);
  }
  finishStream() {
    this.streaming = false;
    m.redraw();
  }
  api(path) {
    return app.forum.attribute('apiUrl') + path;
  }
  loadSuggestions() {
    app.request({
      method: 'GET',
      url: this.api('/ask-suggestions')
    }).then(r => {
      this.suggestions = r.data;
      m.redraw();
    });
  }
  open(slug) {
    this.loading = true;
    this.error = null;
    app.request({
      method: 'GET',
      url: this.api('/ask/' + encodeURIComponent(slug))
    }).then(r => this.show(r.data)).catch(() => this.error = 'That answer could not be found.').finally(() => {
      this.loading = false;
      m.redraw();
    });
  }
  show(a) {
    this.answer = a;
    this.streaming = true;
    this.question = '';
    app.setTitle(a.title);
    if (m.route.param('slug') !== a.slug) m.route.set(app.route('ask.show', {
      slug: a.slug
    }), undefined, {
      replace: true
    });
    window.scrollTo(0, 0);
  }
  ask(e) {
    e?.preventDefault();
    const question = this.question.trim();
    if (question.length < 8 || this.loading) return;
    this.loading = true;
    this.error = null;
    this.answer = null;
    this.waitingIndex = 0;
    this.timer = setInterval(() => {
      this.waitingIndex = Math.min(this.waitingIndex + 1, WAITING.length - 1);
      m.redraw();
    }, 2500);
    app.request({
      method: 'POST',
      url: this.api('/ask'),
      body: {
        question
      },
      errorHandler: () => {}
    }).then(r => this.show(r.data)).catch(err => {
      this.error = err?.response?.errors?.[0]?.detail || 'Something went wrong. Please try again.';
    }).finally(() => {
      clearInterval(this.timer);
      this.loading = false;
      m.redraw();
    });
  }
  publish(on) {
    if (!this.answer) return;
    app.request({
      method: 'POST',
      url: this.api('/ask/' + this.answer.slug + '/publish'),
      body: {
        publish: on
      }
    }).then(r => {
      this.answer.status = r.data.status;
      m.redraw();
    });
  }
  view() {
    return m(PageStructure, {
      className: "AskPage",
      sidebar: () => m(IndexSidebar, null)
    }, m("h1", null, "Ask GuitarTalk"), m("p", null, "Ask anything about guitars, gear and playing. Answers come from 20 years of South African forum threads, with links to the real posts."), m("form", {
      className: "AskBox",
      onsubmit: this.ask.bind(this)
    }, m("input", {
      className: "FormControl",
      placeholder: "e.g. How do I set the intonation on my Strat?",
      maxlength: 300,
      value: this.question,
      oninput: e => this.question = e.target.value,
      disabled: this.loading
    }), m(Button, {
      className: "Button Button--primary",
      type: "submit",
      loading: this.loading,
      disabled: this.question.trim().length < 8
    }, "Ask")), this.error && m("div", {
      className: "AskError"
    }, this.error), !this.answer && !this.loading && this.suggestions.length > 0 && m("div", null, m("h3", null, "Popular questions"), m("div", {
      className: "AskSuggestions"
    }, this.suggestions.map(s => m(Link, {
      className: "Button",
      href: app.route('ask.show', {
        slug: s.slug
      })
    }, s.title)))), this.loading && m("div", null, m(LoadingIndicator, {
      display: "inline"
    }), " ", WAITING[this.waitingIndex]), this.answer && this.renderAnswer(this.answer));
  }
  renderPhotos(a) {
    const photos = a.sources.flatMap(s => (s.images || []).map(im => ({
      ...im,
      source: s
    })));
    if (!photos.length) return null;
    return m("div", {
      className: "AskPhotos"
    }, photos.map(p => m(Link, {
      className: "AskPhoto",
      href: p.source.url + '/' + p.number
    }, m("img", {
      src: p.url,
      alt: p.caption,
      loading: "lazy"
    }), m("span", {
      className: "AskPhoto-caption"
    }, p.caption))));
  }
  renderSource(s) {
    const thumb = s.images?.[0]?.url;
    return m(Link, {
      className: "AskSource",
      href: s.url
    }, m("span", {
      className: "AskSource-n"
    }, s.n), thumb ? m("img", {
      className: "AskSource-thumb",
      src: thumb,
      alt: "",
      loading: "lazy"
    }) : s.starterAvatar ? m("img", {
      className: "AskSource-thumb AskSource-avatar",
      src: s.starterAvatar,
      alt: "",
      loading: "lazy",
      onerror: e => e.target.remove()
    }) : null, m("span", {
      className: "AskSource-text"
    }, m("span", {
      className: "AskSource-title"
    }, s.title), s.hook && m("span", {
      className: "AskSource-hook"
    }, s.hook), m("span", {
      className: "AskSource-meta"
    }, [s.starter && 'Started by ' + s.starter, s.year, s.replies ? s.replies + (s.replies === 1 ? ' reply' : ' replies') : null].filter(Boolean).join(' · '))), m("span", {
      className: "AskSource-go"
    }, "Read \u2192"));
  }
  renderAnswer(a) {
    return m("div", {
      className: 'AskAnswer' + (this.streaming ? ' is-streaming' : '')
    }, m("h2", null, a.title), m("div", {
      className: "AskAnswer-meta"
    }, a.type === 'time_sensitive' ? 'Prices and shops change. Answer checked ' : 'Answer written ', dayjs(a.checkedAt).format('D MMM YYYY'), " from forum threads"), m("div", {
      className: "AskAnswer-body"
    }, [m("div", {
      className: "Post-body",
      key: a.slug,
      oncreate: v => this.stream(v.dom)
    }, m.trust(a.html))]), this.renderPhotos(a), m("div", {
      className: "AskSources"
    }, m("h3", null, "Read the full threads"), m("div", {
      className: "AskSources-list"
    }, a.sources.map(s => this.renderSource(s)))), m("div", {
      className: "AskNote"
    }, "Written by AI from real member posts. Spot something out of date? Reply in the thread and help keep it current."), a.canPublish && m("div", {
      className: "AskNote"
    }, "Admin: ", a.status === 'published' ? 'visible to Google. ' : 'hidden from Google. ', m(Button, {
      className: "Button Button--link",
      onclick: () => this.publish(a.status !== 'published')
    }, a.status === 'published' ? 'Hide from Google' : 'Publish to Google')), m("p", {
      className: "AskAnswer-after"
    }, m(Button, {
      className: "Button",
      onclick: () => m.route.set(app.route('ask'))
    }, "Ask another question")));
  }
}
flarum.reg.add('noriods-ask', 'forum/components/AskPage', AskPage);